import { Button } from "@heroui/react";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import SessionSplash from "@/components/layout/SessionSplash";
import { useAuth } from "@/lib/auth-context";

/**
 * 認証ガード。`GET /auth` の結果で表示を分岐する。
 *
 * - loading  : スプラッシュ。**リダイレクトはしない**。ここで /login 等へ飛ばすと
 *              リロード時に元のパスを失い、判定後にホームへ戻ってしまう
 *              （ngtg_msc の go_router redirect と同じ判断）
 * - unauthed : /login へ。戻り先（query まで含むパス）を state で渡す
 * - error    : 通信断・5xx。Cookie は有効な可能性が高いのでログインへは飛ばさず
 *              再試行を促す（dev は夜間停止するため、停止中を未ログインと
 *              誤判定させない）
 * - authed   : children を表示。ただし**仮パスワードのまま**（must_change_password）
 *              なら、どの画面に行こうとしてもパスワード変更へ送る。API 側も
 *              パスワード変更以外を 403 で弾いており、ここは画面の導線
 */

/** パスワード変更画面のパス。ここだけは強制リダイレクトの対象外にする。 */
export const PASSWORD_CHANGE_PATH = "/password-change";
export default function AuthGate({ children }: { children: ReactNode }) {
  const { status, user, refresh } = useAuth();
  const location = useLocation();

  if (status === "loading") return <SessionSplash />;

  if (status === "unauthed") {
    // **query まで戻り先に含める。** 図面 QR の着地点（/q?v=1&p=...）は query に
    // 対象が入っているので、pathname だけだとログイン後に対象を失った /q に
    // 戻ってしまう（現場で最初に踏む経路）。
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-default-600">
          サーバーに接続できませんでした。
          <br />
          時間をおいて再度お試しください。
        </p>
        <Button color="primary" onPress={() => void refresh()}>
          再試行
        </Button>
      </div>
    );
  }

  // 仮パスワードのままなら、先にパスワードを変えてもらう。変更画面自身は
  // 対象外にしないとリダイレクトが循環する。
  if (user?.must_change_password && location.pathname !== PASSWORD_CHANGE_PATH) {
    return <Navigate to={PASSWORD_CHANGE_PATH} replace />;
  }

  return <>{children}</>;
}
